<script setup lang="ts">
import {computed, onMounted, reactive, ref, watch} from 'vue'
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import validators from '@/utils/validators.ts';
import CardComponent from '@/components/ui/CardComponent.vue';
import AuthService from '@/services/auth/authService.ts';
import { validatePasswordDynamic } from '@/utils/passwordValidations.ts';

import type { ValidationRule } from '@/types/utils/validators.type.ts';
import type { CreatePasswordRequest } from '@/types/services/auth/auth.type.ts';
import type { DynamicValidation} from '@/types/utils/passwordValidations.type.ts';
import AppLogoComponent from '@/components/ui/AppLogoComponent.vue';

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

//--- Define page content ---//
const loading = ref<boolean>(false)
const dynamicValidation = ref<DynamicValidation>({
  length: false,
  upper: false,
  number: false,
  special: false
})

//--- Reset password form ---//
const formValid = ref<boolean>(false)
const form = reactive({
  password: '',
  confirmPassword: '',
})
const showPassword = ref<boolean>(false)
const showConfirmPassword = ref<boolean>(false)
const passwordRules = ref<ValidationRule[]>([...validators.passwordRules])
const confirmPasswordRules = computed(() => [
  ...validators.confirmPasswordRules,
  (v: string) => v === form.password || t('validators.confirmPassword.match')
])
const token = ref<string>('')

watch(() => form.password, (newPassword) => {
  dynamicValidation.value = validatePasswordDynamic(newPassword)
})

function togglePasswordVisibility(): void {
  showPassword.value = !showPassword.value
}

function toggleConfirmPasswordVisibility (): void {
  showConfirmPassword.value = !showConfirmPassword.value
}

async function submitLogin(): Promise<void> {
  loading.value = true

  const userData: CreatePasswordRequest = {
    password: form.password,
    confirmPassword: form.confirmPassword,
    creationAccountToken: token.value
  }

  try {
    await AuthService.createPassword(userData);
    await router.push('/create-password-confirmed');
  } finally {
    loading.value = false
    cleanForm();
  }
}

function cleanForm(): void {
  form.password = ''
  form.confirmPassword = ''
}

onMounted(async (): Promise<void> => {
  const creationAccountToken = route.query.creation_account_token as string
  if (creationAccountToken) {
    token.value = creationAccountToken
  } else {
    await router.push('/login')
  }
})
</script>

<template>
  <v-container fluid class="h-screen w-full d-flex justify-center align-center">
    <CardComponent>
      <template #title>
        <div class="d-flex align-center justify-center my-4">
          <AppLogoComponent />
        </div>
      </template>
      <template #item>
        <v-row>
          <v-col cols="12" class="mb-4">
            <h3>{{ t('views.createPassword.title') }}</h3>
          </v-col>
        </v-row>

        <!-- Password rules -->
        <v-row class="mb-4">
          <v-col>
            <p class="mb-4">
              {{ t('views.userProfile.form.password.rule.description') }}
            </p>
            <ul class="checklist">
              <li class="d-flex align-center">
                <v-icon v-if="dynamicValidation.length" :size="20" class="mr-1 text-success" icon="PhCheck"/>
                <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
                {{ t('views.userProfile.form.password.rule.length') }}
              </li>
              <li class="d-flex align-center">
                <v-icon v-if="dynamicValidation.upper" :size="20" class="mr-1 text-success" icon="PhCheck"/>
                <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
                {{ t('views.userProfile.form.password.rule.upper') }}
              </li>
              <li class="d-flex align-center">
                <v-icon v-if="dynamicValidation.number" :size="20" class="mr-1 text-success" icon="PhCheck"/>
                <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
                {{ t('views.userProfile.form.password.rule.number') }}
              </li>
              <li class="d-flex align-center">
                <v-icon v-if="dynamicValidation.special" :size="20" class="mr-1 text-success" icon="PhCheck"/>
                <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
                {{ t('views.userProfile.form.password.rule.special') }}
              </li>
            </ul>
          </v-col>
        </v-row>

        <!-- Create password form -->
        <v-form v-model="formValid" @submit.prevent="submitLogin">
          <v-row class="py-1">
            <v-col cols="12">
              <v-text-field
                v-model="form.password"
                :append-inner-icon="showPassword ? 'PhEyeSlash' : 'PhEye'"
                :label="t('views.createPassword.inputs.password.label')"
                :placeholder="t('common.inputs.password.placeholder')"
                :rules="passwordRules"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="password"
                hide-details="auto"
                @click:append-inner="togglePasswordVisibility"
              />
            </v-col>
          </v-row>
          <v-row class="py-1">
            <v-col cols="12">
              <v-text-field
                v-model="form.confirmPassword"
                :append-inner-icon="showConfirmPassword ? 'PhEyeSlash' : 'PhEye'"
                :label="t('views.createPassword.inputs.confirmPassword.label')"
                :placeholder="t('common.inputs.password.placeholder')"
                :rules="confirmPasswordRules"
                :type="showConfirmPassword ? 'text' : 'password'"
                autocomplete="password"
                hide-details="auto"
                @click:append-inner="toggleConfirmPasswordVisibility"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                :disabled="!formValid || loading"
                :loading="loading"
                block
                type="submit"
              >
                {{ t('views.createPassword.buttons.create') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </CardComponent>
  </v-container>
</template>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
